import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/classnames.js';
import { currentVideoTier } from '../lib/videoTier.js';

/**
 * ForestScrub — the Buy/leaves page's immersive scroll scene.
 *
 * The Seedance avenue is SCROLL-SCRUBBED: scroll position is the video's clock.
 * Scroll down and every leaf, branch and shaft of light moves forward exactly as
 * in the clip; scroll up and it runs perfectly in reverse. Nothing autoplays, so
 * there's no loop-back jump — you are always exactly where your scroll puts you.
 *
 * Flawless two-way scrubbing needs every frame to be independently seekable, so
 * the source is re-encoded to ALL-KEYFRAMES (avenue-scrub.mp4) — a currentTime
 * seek to any moment is a single intra-frame decode, instant in both directions.
 * The plate stays full 1080p (no recompression to a frame sheet), so it's sharp.
 * A whisper of scroll-driven push-in toward the light adds depth without softening.
 *
 * Structure mirrors CorridorScrub: a tall outer <section> (heightVh) with a
 * sticky 100vh inner holding the <video>, a gold bloom that pulses at the scene
 * beats, a legibility scrim, and the page's content as `children`; the smoothed
 * scroll progress (0→1) is reported via `onProgress`. Reduced-motion is handled
 * by the PARENT (it renders the plain readable page instead).
 */
export function ForestScrub({
  src,
  mobileSrc,
  poster,
  heightVh = 580,
  className,
  children,
  onProgress,
  portals = [0.34, 0.66],
}: {
  src: string;
  /** Lighter ~720p re-encode served to phones / constrained connections. The
   *  avenue is all-keyframes and this scrub uses exact currentTime seeks, so a
   *  short-GOP mobile tier drops in with no seek-path change. */
  mobileSrc?: string;
  poster: string;
  heightVh?: number;
  className?: string;
  children?: ReactNode;
  onProgress?: (p: number) => void;
  portals?: number[];
}) {
  // Resolve the tier once at mount — pointer type doesn't change on rotation,
  // so a single decision holds for the session.
  const [tier] = useState(currentVideoTier);
  const resolvedSrc = mobileSrc && tier === 'mobile' ? mobileSrc : src;
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const bloomRef = useRef<HTMLDivElement>(null);
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    if (!section || !video) return undefined;

    let duration = 0;
    let ready = false;
    const onMeta = (): void => {
      duration = video.duration || 10;
    };
    const onData = (): void => {
      ready = true;
    };
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('loadeddata', onData);
    if (video.readyState >= 1) onMeta();
    if (video.readyState >= 2) ready = true;

    // The scene is scroll-scrubbed. On load it plays one brief, slow INTRO drift —
    // a slight forward move that introduces the "scroll to move" feel (like the
    // corridor) AND activates the decode pipeline so currentTime seeks render
    // afterwards. It stops the instant you scroll, or after ~1.3s, and never plays
    // again: from then on, scroll is the only clock.
    video.muted = true;
    video.playbackRate = 0.6;
    let scrubbing = false;
    let introTimer = 0;
    const beginScrub = (): void => {
      if (scrubbing) return;
      scrubbing = true;
      window.clearTimeout(introTimer);
      video.pause();
    };
    void video
      .play()
      .then(() => {
        introTimer = window.setTimeout(beginScrub, 1300);
      })
      .catch(() => {
        scrubbing = true; // autoplay blocked → straight to scroll control
      });

    const compute = (): number => {
      const r = section.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      return total > 0 ? Math.max(0, Math.min(1, -r.top / total)) : 0;
    };

    let target = compute();
    let shown = target;
    let raf = 0;

    const ease = (x: number): number => 1 - Math.pow(1 - x, 2.1);
    const MAXZOOM = 1.14; // whisper of depth — kept tiny so the plate stays crisp

    const onScroll = (): void => {
      beginScrub();
      target = compute();
    };

    const loop = (): void => {
      raf = requestAnimationFrame(loop);
      if (!scrubbing) {
        // Intro playing — follow the video so the hand-off to scroll is seamless.
        shown = duration > 0 ? Math.min(0.9999, video.currentTime / duration) : 0;
        target = shown;
      } else {
        // Track the scroll closely (light inertia) so motion reads as direct scrub.
        shown += (target - shown) * 0.18;
        if (ready && duration > 0) {
          const t = Math.max(0, Math.min(duration - 0.04, shown * duration));
          if (Math.abs(video.currentTime - t) > 0.012) video.currentTime = t;
        }
      }
      const p = shown;
      onProgressRef.current?.(p);

      const zoom = 1.0 + (MAXZOOM - 1.0) * ease(p);
      video.style.transform = `scale(${zoom.toFixed(4)})`;

      const bloom = bloomRef.current;
      if (bloom) {
        let b = 0;
        for (const q of portals) b = Math.max(b, Math.exp(-Math.pow((p - q) / 0.05, 2)));
        const arrival = Math.max(0, (p - 0.82) / 0.18);
        bloom.style.opacity = Math.min(1, b * 0.4 + arrival * arrival * 0.32).toFixed(3);
      }
    };
    raf = requestAnimationFrame(loop);

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(introTimer);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('loadeddata', onData);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className={cn('relative', className)}
      style={{ height: `${heightVh}vh`, backgroundColor: '#05040a' }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          width: '100%',
          overflow: 'hidden',
          backgroundColor: '#05040a',
        }}
      >
        <video
          ref={videoRef}
          src={resolvedSrc}
          poster={poster}
          muted
          playsInline
          preload="auto"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover will-change-transform"
          style={{ transformOrigin: '50% 41%' }}
        />
        {/* gold-light bloom — pulses at the scene beats + swells at arrival */}
        <div
          ref={bloomRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-[4]"
          style={{
            opacity: 0,
            background:
              'radial-gradient(58% 50% at 50% 41%, rgba(255,243,206,0.9), rgba(255,214,140,0.38) 38%, rgba(255,200,110,0) 72%)',
            mixBlendMode: 'screen',
          }}
        />
        <ForestScrim />
        {children}
      </div>
    </section>
  );
}

/** Cinematic vignette + top/bottom gradient so overlaid type reads over the
 *  bright canopy; deeper at the foot where the tier cards + checkout sit. */
function ForestScrim() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-[5]"
      style={{
        background:
          'radial-gradient(130% 100% at 50% 40%, transparent 26%, rgba(5,4,10,0.3) 66%, rgba(5,4,10,0.7) 100%),' +
          'linear-gradient(180deg, rgba(5,4,10,0.48) 0%, rgba(5,4,10,0.04) 22%, rgba(5,4,10,0.08) 48%, rgba(5,4,10,0.86) 100%)',
      }}
    />
  );
}
